/*
 * Orizza — shared decorative-rule scroll behavior. Every section heading's
 * <span class="orizza-rule" data-orizza-rule> (see snippets/orizza-rule.liquid)
 * grows and fades in as its section approaches the vertical center of the
 * viewport, shrinks back down as it moves away, and is fully invisible
 * once the section is off-screen in either direction — one scroll listener
 * driving every rule on the page, rather than each section wiring its own.
 *
 * The growth/shrink is scaled to the section's OWN height, not the
 * viewport's: progress ramps 0 → 1 while the viewport's center line
 * sweeps across the section's first half (top edge to center), peaks at
 * exactly 1 when it's dead-center, then ramps back 1 → 0 across the
 * section's second half (center to bottom edge). So the full grow-then-
 * shrink cycle always spans exactly one section's height, regardless of
 * viewport size.
 */
(function () {
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setup() {
    var pairs = Array.prototype.slice
      .call(document.querySelectorAll('[data-orizza-rule]'))
      .map(function (rule) {
        var section = rule.closest('.shopify-section') || rule.closest('section');
        return section ? { section: section, rule: rule } : null;
      })
      .filter(Boolean);

    // Gold strip on section CTAs (.orizza-shine in assets/orizza-rule.css) —
    // slides left → right just after the CTA scrolls in from the bottom
    // (scrolling down), right → left just after it scrolls in from under
    // the header (scrolling up), and is hidden outside those sweeps.
    var shines = Array.prototype.slice
      .call(document.querySelectorAll('[data-orizza-shine]'))
      .map(function (el) {
        var section = el.closest('.shopify-section') || el.closest('section');
        return section ? { section: section, el: el } : null;
      })
      .filter(Boolean);

    // "View all" / "Shop All" blade sweep (.orizza-blade in
    // assets/orizza-rule.css) — loops only while its section is on screen.
    var blades = document.querySelectorAll('[data-orizza-blade]');
    if (blades.length && 'IntersectionObserver' in window) {
      var bladeObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.__orizzaBlades.forEach(function (el) {
            el.classList.toggle('is-in-view', entry.isIntersecting);
          });
        });
      }, { threshold: 0.15 });
      Array.prototype.forEach.call(blades, function (el) {
        var section = el.closest('.shopify-section') || el.closest('section') || el;
        if (!section.__orizzaBlades) {
          section.__orizzaBlades = [];
          bladeObserver.observe(section);
        }
        section.__orizzaBlades.push(el);
      });
    }

    if (!pairs.length && !shines.length) return;

    // Respect reduced motion by just leaving every rule at full size/opacity
    // (the property's own default) rather than animating it on scroll.
    if (prefersReducedMotion) return;

    var ticking = false;
    var lastScrollY = window.scrollY;
    var scrollingUp = false;

    function update() {
      ticking = false;
      var scrollY = window.scrollY;
      if (scrollY !== lastScrollY) scrollingUp = scrollY < lastScrollY;
      lastScrollY = scrollY;
      var viewportCenter = window.innerHeight / 2;

      pairs.forEach(function (pair) {
        var rect = pair.section.getBoundingClientRect();
        var sectionCenter = rect.top + rect.height / 2;
        // Half the section's own height — the viewport center is exactly
        // this far from the section's center when it's crossing the
        // section's top or bottom edge.
        var maxDistance = rect.height / 2;
        if (maxDistance <= 0) return;
        var distance = Math.abs(sectionCenter - viewportCenter);
        var progress = 1 - Math.min(distance / maxDistance, 1);
        pair.rule.style.setProperty('--orizza-rule-progress', progress.toFixed(3));
      });

      // The fixed header covers the top of the viewport, so a CTA scrolling
      // down into view (scrolling up) only becomes visible below it.
      var header = document.querySelector('.orizza-header, .frost-header');
      var visibleTop = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

      shines.forEach(function (shine) {
        // Timed to the CTA itself, not the section's edges, so the sweep
        // always plays while the CTA is actually on screen. Sweep length
        // is a quarter of the section's height.
        var travel = shine.section.getBoundingClientRect().height / 4;
        if (travel <= 0) return;
        var rect = shine.el.getBoundingClientRect();
        var pos;
        if (scrollingUp) {
          // 1 when the CTA appears from under the header, 0 once it has
          // moved a quarter-section further down.
          pos = 1 - (rect.top - visibleTop) / travel;
        } else {
          // 0 when the CTA appears at the bottom of the viewport, 1 once
          // it has moved a quarter-section further up.
          pos = (window.innerHeight - rect.bottom) / travel;
        }
        pos = Math.max(0, Math.min(1, pos));
        shine.el.style.setProperty('--orizza-shine-pos', pos.toFixed(3));
        // Only visible mid-sweep, not parked at either edge.
        shine.el.style.setProperty('--orizza-shine-opacity', pos > 0 && pos < 1 ? '1' : '0');
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
